"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getCookie, setCookie } from "@repo/client-cookies";
import { BRAND_TO_FLOOR_MAP } from "@/constants";
import { getFloorCode, Floor, FloorCode, determineFloor } from "./utils";

export type FloorContextType = {
  floor: Floor | null;
  code: FloorCode | null;
  setFloor: (floor: Floor) => void;
};

const FloorContext = createContext<FloorContextType | null>(null);

const FLOOR_COOKIE_NAME = "floor";

export const FloorProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const [floor, setFloorState] = useState<Floor | null>(null);

  useEffect(() => {
    const determinedFloor = determineFloor(
      getCookie(FLOOR_COOKIE_NAME) || "",
      pathname || "",
    );

    if (determinedFloor) {
      setFloorState(determinedFloor);
      return;
    }
  }, [pathname]);

  const setFloor = useCallback((newFloor: Floor) => {
    const floorCode = getFloorCode(newFloor);

    setCookie(FLOOR_COOKIE_NAME, floorCode, {
      path: "/",
      expiresInMinutes: 60 * 60 * 24 * 365,
    });

    setFloorState(newFloor);
  }, []);

  const floorCode =
    floor && Object.keys(BRAND_TO_FLOOR_MAP).includes(floor)
      ? getFloorCode(floor as Floor)
      : null;

  const contextValue: FloorContextType = {
    floor,
    code: floorCode as FloorCode | null,
    setFloor,
  };

  return (
    <FloorContext.Provider value={contextValue}>
      {children}
    </FloorContext.Provider>
  );
};

export const useFloorContext = () => {
  const context = useContext(FloorContext);

  if (!context) {
    throw new Error("useFloorContext must be used within a FloorProvider");
  }

  return context;
};
